import { useEffect, useMemo, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import { showingRange } from '@/pages/services/components/RowMenu';
import type { WaapTableChart, WaapTableRow } from '@/api/waap';
import styles from './DetailsLogTable.module.css';

/** Rows per page. The BE returns the whole window at once (row limit 100); a
 *  short page keeps the table — and the horizontal scrollbar under it — on one
 *  screen. */
const PAGE_SIZE = 13;

/** Column widths in px, keyed by the BE's column key (the label lowercased with
 *  spaces as underscores). The stylesheet lays the table out fixed, so these are
 *  what every column gets: a cell unfolding on hover then re-flows nothing —
 *  under auto layout it collapsed to its wrapped width and the neighbours
 *  jumped sideways to absorb the slack. Unlisted columns take the default. */
const COLUMN_WIDTHS: Record<string, number> = {
  time: 160,
  host: 170,
  region: 130,
  origin_status: 120,
  origin_ip: 170,
  origin_response_time: 180,
  request_id: 300,
  path: 320,
  status: 90,
  attack_type: 140,
  inject_rule_id: 120,
  inject_attack_type: 160,
  source_ip: 150,
  inject_payload: 200,
  args: 200,
  referer: 200,
  in_whitelist_ip: 140,
  user_agent: 260,
  manual_rule_id: 150,
  http_method: 130,
};
const DEFAULT_COLUMN_WIDTH = 150;

/**
 * The Threat Investigation "Details" access-log table, pulled out of the generic
 * WaapChartGrid so each row is clickable → opens the Log Detail modal. The log
 * is wide (20 cols) → horizontal scroll.
 *
 * Paged client-side after a customer report: every record on one page made the
 * table thousands of pixels tall, so the horizontal scrollbar sat far below the
 * fold and reading a right-hand column meant scrolling to the very bottom,
 * dragging sideways, then scrolling back up.
 */
export function DetailsLogTable({
  chart,
  onRowClick,
}: {
  chart: WaapTableChart;
  onRowClick: (row: WaapTableRow) => void;
}) {
  const rows = useMemo(() => chart.rows ?? [], [chart.rows]);
  const [page, setPage] = useState(1);

  // A new window / filter / refresh returns a fresh row set — start over at the
  // newest records rather than stranding the reader on a page that is now empty.
  useEffect(() => setPage(1), [rows]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const start = (current - 1) * PAGE_SIZE;
  const paged = useMemo(() => rows.slice(start, start + PAGE_SIZE), [rows, start]);

  const columns = useMemo<ColumnDef<WaapTableRow, unknown>[]>(
    () =>
      (chart.columns ?? [])
        // `timestamp_ms` is a hidden helper column: the BE emits raw epoch millis
        // only so the click handler can open the Log Detail modal — not for display.
        .filter((col) => col.key !== 'timestamp_ms')
        .map((col) => ({
          accessorKey: col.key,
          header: col.label,
          size: COLUMN_WIDTHS[col.key] ?? DEFAULT_COLUMN_WIDTH,
          // Access logs read best raw (status codes, IPs, paths, timing) — no
          // numeric thousands-formatting like the generic dashboard tables do.
          // A cut-off value unfolds on hover (see the stylesheet), so it stays
          // plain selectable text rather than a tooltip nobody can copy from.
          cell: ({ row }) => {
            const raw = row.original[col.key];
            return raw == null || raw === '' ? '—' : String(raw);
          },
        })),
    [chart.columns],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{chart.title}</CardTitle>
      </CardHeader>
      <CardBody>
        <div className={styles.scroll}>
          <DataTable
            columns={columns}
            data={paged}
            onRowClick={onRowClick}
            emptyState={
              <EmptyState
                size="sm"
                icon={<ScrollText size={20} />}
                title="No log entries"
                description="No access-log records match the selected window."
              />
            }
            footer={rows.length ? showingRange(current, PAGE_SIZE, paged.length, rows.length) : undefined}
          />
        </div>
        {pageCount > 1 && (
          <div className={styles.pagination}>
            <Pagination page={current} pageCount={pageCount} onPageChange={setPage} />
          </div>
        )}
      </CardBody>
    </Card>
  );
}
